import { getLabLostAndFound, LabLostAndFoundParams, LabLostAndFoundResponse } from 'api/labs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { flatMap, uniqBy } from 'lodash';
import React from 'react';

const PROGRESSIVE_LOAD_PAGE_SIZE = 100;

export const getLabLostAndFoundQueryKey = (labId: string, params: LabLostAndFoundParams) => [
  'labLostAndFound',
  labId,
  params,
];

export const useLabLostAndFound = (labId: string, params: LabLostAndFoundParams) => {
  const query = useInfiniteQuery(
    getLabLostAndFoundQueryKey(labId, params),
    ({ pageParam, signal }) =>
      getLabLostAndFound(
        labId,
        {
          ...params,
          pageSize: PROGRESSIVE_LOAD_PAGE_SIZE,
          cursor: pageParam,
        },
        signal
      ),
    {
      enabled: Boolean(labId),
      getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor || undefined : undefined),
    }
  );

  React.useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage && !query.isError) {
      query.fetchNextPage();
    }
  }, [query.fetchNextPage, query.hasNextPage, query.isError, query.isFetchingNextPage]);

  const lostUploads = uniqBy(
    flatMap(query.data?.pages || [], ({ lostUploads: pageUploads }) => pageUploads),
    'objectKey'
  );
  const lastPage = query.data?.pages.at(-1);
  const data: LabLostAndFoundResponse | undefined = lastPage
    ? {
        lostUploads,
        nextCursor: lastPage.nextCursor,
        hasMore: lastPage.hasMore,
      }
    : undefined;

  return {
    ...query,
    data,
    loadedPageCount: query.data?.pages.length || 0,
    isProgressivelyLoading: query.isLoading || query.isFetchingNextPage || Boolean(query.hasNextPage),
  };
};
